import { Backdrop, Button, ThemeToggle } from '@memorizecards/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { IconLogout, IconUser } from './Icons';
import { Logo } from './Logo';
import { useAuth } from '../state/auth';

const NAV = [
  { to: '/admin', rotulo: 'Visão geral', exact: true },
  { to: '/admin/usuarios', rotulo: 'Usuários' },
  { to: '/admin/uso', rotulo: 'Uso' },
  { to: '/admin/gratis', rotulo: 'Flashcards grátis' },
  { to: '/admin/pedidos', rotulo: 'Pedidos' },
  { to: '/admin/pagamentos', rotulo: 'Pagamentos' },
  { to: '/admin/cupons', rotulo: 'Cupons' },
  { to: '/admin/cadastros', rotulo: 'Cadastros (cupom 5%)' },
  { to: '/admin/feedbacks', rotulo: 'Feedbacks' },
  { to: '/admin/etl', rotulo: 'Flashcards' },
  { to: '/admin/backup', rotulo: 'Backup' },
  { to: '/admin/seguranca', rotulo: 'Segurança' },
  { to: '/admin/emails', rotulo: 'E-mails' },
  { to: '/admin/auditoria', rotulo: 'Auditoria' },
];

const ITEM = Object.fromEntries(NAV.map((n) => [n.to, n]));
const GRUPOS: { rotulo: string; tos: string[] }[] = [
  { rotulo: 'Vendas', tos: ['/admin/pedidos', '/admin/pagamentos', '/admin/cupons', '/admin/cadastros'] },
  { rotulo: 'Conteúdo', tos: ['/admin/gratis', '/admin/etl'] },
  { rotulo: 'Acompanhamento', tos: ['/admin/uso', '/admin/feedbacks', '/admin/emails'] },
  { rotulo: 'Sistema', tos: ['/admin/backup', '/admin/seguranca', '/admin/auditoria'] },
];

function GrupoAdmin({ rotulo, tos }: { rotulo: string; tos: string[] }): ReactNode {
  const [aberto, setAberto] = useState(false);
  const { pathname } = useLocation();
  const ativo = tos.some((t) => pathname.startsWith(t));
  useEffect(() => setAberto(false), [pathname]);
  useEffect(() => {
    if (!aberto) return;
    const f = (e: KeyboardEvent) => e.key === 'Escape' && setAberto(false);
    window.addEventListener('keydown', f);
    return () => window.removeEventListener('keydown', f);
  }, [aberto]);
  return (
    <div className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={aberto}
        onClick={() => setAberto((v) => !v)}
        className={[
          'flex items-center gap-2 whitespace-nowrap rounded-mc-pill px-3 py-2 text-sm transition-colors',
          ativo ? 'bg-mc-accent/12 text-mc-accent ring-1 ring-mc-accent/40' : 'text-mc-ink2 hover:text-mc-ink',
        ].join(' ')}
      >
        {rotulo}
        <span aria-hidden className="text-xs">▾</span>
      </button>
      {aberto ? (
        <>
          <button
            type="button"
            aria-label="Fechar submenu"
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setAberto(false)}
          />
          <div
            role="menu"
            className="absolute left-0 top-full z-50 mt-2 min-w-[200px] rounded-mc-field border border-mc-line bg-mc-surface-nav-solid p-1 shadow-lg"
          >
            {tos.map((t) => (
              <NavLink
                key={t}
                role="menuitem"
                to={t}
                className="block whitespace-nowrap rounded-mc-field px-3 py-2 text-sm text-mc-ink2 hover:bg-mc-surface-hover hover:text-mc-ink"
              >
                {ITEM[t]?.rotulo}
              </NavLink>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

export function AdminShell(): ReactNode {
  const { user, sair } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="relative min-h-screen">
      <Backdrop />

      <header className="sticky top-0 z-40 border-b border-mc-line bg-mc-surface-nav backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-mc-content items-center gap-4 px-4 sm:px-6">
          <NavLink to="/admin" className="shrink-0">
            <Logo />
          </NavLink>
          <span className="hidden rounded-mc-pill border border-mc-accent/40 bg-mc-accent/10 px-3 py-1 text-xs font-medium text-mc-accent sm:inline">
            Superadmin
          </span>

          <nav className="ml-2 hidden min-w-0 flex-1 items-center gap-1 xl:flex" aria-label="Admin">
            {['/admin', '/admin/usuarios'].map((to) => (
              <NavLink
                key={to}
                to={to}
                end={ITEM[to]?.exact}
                className={({ isActive }) =>
                  [
                    'whitespace-nowrap rounded-mc-pill px-3 py-2 text-sm transition-colors',
                    isActive
                      ? 'bg-mc-accent/12 text-mc-accent ring-1 ring-mc-accent/40'
                      : 'text-mc-ink2 hover:text-mc-ink',
                  ].join(' ')
                }
              >
                {ITEM[to]?.rotulo}
              </NavLink>
            ))}
            {GRUPOS.map((g) => (
              <GrupoAdmin key={g.rotulo} rotulo={g.rotulo} tos={g.tos} />
            ))}
          </nav>

          <div className="ml-auto flex shrink-0 items-center gap-2 xl:ml-0">
            <ThemeToggle size="sm" />
            <NavLink
              to="/baralhos"
              className="hidden text-sm text-mc-ink2 underline-offset-4 hover:text-mc-accent hover:underline sm:inline"
            >
              Plataforma
            </NavLink>
            <span className="hidden items-center gap-2 text-sm text-mc-ink2 2xl:inline-flex">
              <IconUser className="size-4" />
              {user?.email}
            </span>
            <Button
              variant="quiet"
              size="sm"
              onClick={() => void sair().then(() => navigate('/entrar'))}
            >
              <IconLogout className="size-4" />
            </Button>
          </div>
        </div>

        <nav
          className="flex gap-1 overflow-x-auto border-t border-mc-line px-4 py-2 xl:hidden"
          aria-label="Admin móvel"
        >
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              className={({ isActive }) =>
                [
                  'shrink-0 rounded-mc-pill px-3 py-1.5 text-xs',
                  isActive ? 'bg-mc-accent/12 text-mc-accent' : 'text-mc-ink2',
                ].join(' ')
              }
            >
              {item.rotulo}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="relative z-[2] mx-auto max-w-mc-content px-4 py-8 sm:px-6">
        <Outlet />
      </main>
    </div>
  );
}
